import { useCallback } from 'react';
import { creatorbaseQueries, youtube, youtubeQueries } from '@round/api';
import { isNumber } from 'utility/utility';
import { backupPostsStatuses } from 'Modules/Plans/Posts/helpers';
import { useQueryClient } from '@tanstack/react-query';
import usePublicReportId from 'Modules/Plans/PublicReports/usePublicReportId';
import { useYoutubeChannelsListQueries } from '@round/platforms/youtube';
import uniq from 'lodash/uniq';
import { useInfinitePosts, usePostsTotals } from '@round/creatorbase-helpers/api-hooks';

// Posts page size, matching the account-id batch size the other platforms' posts hooks use, so all
// three tables page at the same size.
const BATCH_SIZE = 50;

// fetchImmediate needs every post in one response, so it reads far past a table page.
const FETCH_IMMEDIATE_PAGE_SIZE = 1000;

// Keeps infinite posts, totals, and imperative posts aligned in terms of params
const buildPostsParams = (campaignId: number | undefined, publicReportId: string | undefined) => ({
    campaign_id: campaignId?.toString(),
    ordering: '-view_count' as const,
    exclude_statuses: backupPostsStatuses.join(','),
    platform: 'youtube' as const,
    publicReportId,
});

// Totals must cover exactly the list's filtered set, so they're derived from the list params rather
// than built independently.
const buildPostsTotalsParams = (campaignId: number | undefined, publicReportId: string | undefined) => {
    const { ordering, ...params } = buildPostsParams(campaignId, publicReportId);
    return params;
};

// The same params feed the declarative read below and the imperative fetchImmediate prime, so both
// paths hit identical query keys and share cache entries.
const buildChannelParams = (channelIds: number[], publicReportId: string | undefined) => ({
    id: channelIds.join(','),
    page_size: channelIds.length,
    publicReportId,
});

const getChannelIds = (posts: { youtube_details: { account_id: number | null } | null }[]) =>
    uniq(posts.map((post) => post.youtube_details?.account_id).filter(isNumber));

// Rows are partial until every page is loaded, so the footers always read API totals rather than
// reducing over the rendered rows.
const collectLoadingPageIndexes = (results: { isLoading: boolean }[]) =>
    new Set(results.flatMap((result, index) => (result.isLoading ? [index] : [])));

export function usePublicReportYoutubePosts(
    campaignId: number | undefined,
    { enabled = true }: { enabled?: boolean } = {}
) {
    const publicReportId = usePublicReportId();
    const queryClient = useQueryClient();
    const isReady = enabled && Boolean(publicReportId) && Boolean(campaignId);

    const infinitePosts = useInfinitePosts(
        { ...buildPostsParams(campaignId, publicReportId), page_size: BATCH_SIZE },
        { enabled: isReady }
    );

    // isLoading, not isFetching: a background refetch would otherwise skeleton every footer while the
    // row cells keep their values.
    const { data: postsTotals, isLoading: areTotalsLoading } = usePostsTotals(
        buildPostsTotalsParams(campaignId, publicReportId),
        { enabled: isReady }
    );

    const postPages = (infinitePosts.data?.pages ?? []).map((page) => page.results);
    const posts = postPages.flat();

    // Channel ids are batched by posts page, so loading another page never changes an earlier page's
    // query key. Every page issues a query — disabled when the page has no channels — so a query's
    // index is its page index.
    const channelIdPages = postPages.map(getChannelIds);

    const { data: channelsData, loadingPageIndexes: loadingChannelPageIndexes } = useYoutubeChannelsListQueries(
        channelIdPages.map((ids) => ({
            params: buildChannelParams(ids, publicReportId),
            config: { enabled: ids.length > 0 },
        })),
        {
            combine: (results) => ({
                data: new Map(results.flatMap((result) => result.data?.results ?? []).map((c) => [c.id, c])),
                loadingPageIndexes: collectLoadingPageIndexes(results),
            }),
        }
    );

    // Copy-to-clipboard needs every post plus its channel data, however few pages the table has
    // loaded, so it primes its own full-set query keys. ensureQueryData reuses the shared cache and
    // only fetches what is missing.
    const fetchImmediate = useCallback(async () => {
        if (!campaignId) {
            return null;
        }

        const postsResponse = await queryClient.ensureQueryData(
            creatorbaseQueries.posts.options.list({
                ...buildPostsParams(campaignId, publicReportId),
                page_size: FETCH_IMMEDIATE_PAGE_SIZE,
            })
        );
        const posts = postsResponse.results;

        const channelIds = getChannelIds(posts);
        if (!channelIds.length) {
            return { posts, channels: [] as youtube.Channel[] };
        }

        try {
            const channelsResponse = await queryClient.ensureQueryData(
                youtubeQueries.channels.options.list(buildChannelParams(channelIds, publicReportId))
            );
            return { posts, channels: channelsResponse.results };
        } catch {
            return { posts, channels: [] as youtube.Channel[] };
        }
    }, [campaignId, publicReportId, queryClient]);

    const pageIndexByPostId = new Map(
        postPages.flatMap((pagePosts, pageIndex) => pagePosts.map((post) => [post.id, pageIndex] as const))
    );
    // A row's loading state is its own page's, so a later page's fetch never skeletons earlier rows.
    const getIsAccountDataLoading = (post: { id: string }) => {
        const pageIndex = pageIndexByPostId.get(post.id);
        if (pageIndex === undefined) {
            return false;
        }

        return loadingChannelPageIndexes.has(pageIndex);
    };

    return {
        posts,
        isPostsLoading: infinitePosts.isLoading,
        isPostsError: infinitePosts.isError,
        channelsData,
        getIsAccountDataLoading,
        fetchImmediate,
        postsTotals,
        areTotalsLoading,
        hasNextPage: infinitePosts.hasNextPage,
        isFetchingNextPage: infinitePosts.isFetchingNextPage,
        fetchNextPage: infinitePosts.fetchNextPage,
    };
}
